import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import Sidebar from './Sidebar';
import ChatWindow from './ChatWindow';
import { getProjects, getStage, getChatHistory, getFullProposal } from './api';
import { sendLogin } from './authentication_api.js';
import { getContents, idea2Project } from '../ProjectWriting/api.js';
import { useNavigate } from 'react-router-dom';
import './ChatPage.css';

function ChatPage() {
  const [user] = useAuthState(auth);
  const [projects, setProjects] = useState([]);
  const [credit, setCredit] = useState([]);

  const [selectedProject, setSelectedProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [messageLoadingProject, setMessageLoadingProject] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmProject, setConfirmProject] = useState(null);
  const [keywordsList, setKeywordsList] = useState([""]);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    async function initChat() {
      try {
        setMessageLoadingProject("Kullanıcı bilgileri doğrulanıyor ve Projeler listeleniyor...");
        setLoadingProject(true);
        // ✅ Step 1: Ensure backend login (token present and valid)
        await ensureBackendLogin(user);

        // ✅ Step 2: Fetch projects after token is valid
        const data = await getProjects(user.uid);
        setProjects(data.idea_list);
        setCredit(data.credit);
        
        setMessageLoadingProject("");
        setLoadingProject(false);
      } catch (err) {
        console.error("Failed during chat init (login or data fetch):", err);
      }
    }

    initChat();
    
  }, [user]);

  const ensureBackendLogin = async (user) => {
    const token = localStorage.getItem('authToken');

    const isTokenValid = async () => {
      if (!token) return false;

      try {
        await getProjects(user.uid);
        return true; 
      } catch (err) {
        if (err.response && err.response.status === 401) {
          console.warn("Token is invalid or expired");
          return false;
        }
        throw err; 
      }
    };

    if (!(await isTokenValid())) {
      try {
        await sendLogin(user.uid); 
        console.log("Token refreshed via backend login");
      } catch (err) {
        console.error("Backend login failed:", err);
        throw err;
      }
    }
  };

  const continueProjectFlow = async (project) => {
    const res = await getStage(project.idx);
    const stage = res.stage;
    
    if (stage === 2) {
      navigate('/sections_writing', {
        state: {
          user_idx: user.uid,
          idea_idx: project.idx,
          title: project.title,
          method: res.proposal,
          keywordsList,
          priority: res.priority_text,
          wpList: res.wp_list,
          publicationList: res.publications,
          projectDuration: res.project_duration
        }
      });
      return;
    }
    else if (stage === 1) {
      navigate('/project_writing_inputs', {
        state: {
          idea_idx: project.idx,
          title: project.title,
          method: res.proposal,
          priority: res.priority_text,
          wpList: res.wp_list,
          publicationList: res.publications,
          projectDuration: res.project_duration,
        },
      });
      return;
    }

    const { title, messages } = await getChatHistory(project.idx);

    if (messages[messages.length - 1].text === 'COMPLETED') {
      navigate('/project_preparation', {
        state: { idea_idx: project.idx, title: project.title, min_duration: 10000 },
      });
      return;
    }

    setSelectedProject({ ...project, title });
    setMessages(messages);
    setLoading(false);
  };

  const handleProjectSelect = async (project) => {
    setMessageLoadingProject("Proje aşamaları kontrol ediliyor...");
    setLoadingProject(true);
    const project_idx = await idea2Project(project.idx);

    if (project_idx !== -1 && project_idx !== null && project_idx !== undefined) {
      const sections_content = await getContents(project_idx);
      const emptyKeys = Object.keys(sections_content).filter(
        (key) => 
          sections_content[key] === '' && 
          key !== 'PRIORITY' && 
          key !== 'REASON_PRIORITY'
      );

      if (emptyKeys.length === 0) {
        navigate('/project_writing_user', {
          state: {
            project_idx,
            title: project.title,
            method: sections_content.METHOD,
            priority: sections_content.PRIORITY,
            wpList: sections_content.TASKS,
            sectionContent: sections_content,
          },
        });
        return;
      }

      setConfirmProject({ project, project_idx, sections_content });
      
      setMessageLoadingProject("");
      setLoadingProject(false);
      setShowConfirm(true);
      return;
    }

    await continueProjectFlow(project);
    setMessageLoadingProject("");
    setLoadingProject(false);
  };

  const handleConfirmYes = () => {
    if (confirmProject) {
      continueProjectFlow(confirmProject.project);
    }
    setShowConfirm(false);
  };

  const handleConfirmNo = () => {
    if (confirmProject) {
      const { project, project_idx, sections_content } = confirmProject;
      navigate('/project_writing_user', {
        state: {
          project_idx,
          title: project.title,
          method: sections_content.METHOD,
          priority: sections_content.PRIORITY,
          wpList: sections_content.TASKS,
          sectionContent: sections_content,
        },
      });
    }
    setShowConfirm(false);
  };

  if (!user) return null;

  return (
    <div className="app-container">
      <img src="/logo_light_unified_noBG.png" alt="Logo" className="top-logo" />
      
      {loadingProject && (
      <div className="loading-overlay">
        <div className="spinner" />
        <p>{messageLoadingProject}</p>
      </div>
      )}
      <div className="chat-container">
        <Sidebar
          projects={projects}
          onProjectSelect={handleProjectSelect}
          setProjects={setProjects}
          setSelectedProject={setSelectedProject}
          setMessages={setMessages}
          credit={credit}
          user_idx={user.uid}
        />
        <ChatWindow
          user={user}
          project={selectedProject}
          messages={messages}
          setMessages={setMessages}
          setProjects={setProjects}
          setSelectedProject={setSelectedProject}
          loading={loading}
          setLoading={setLoading}
        />
      </div>

      {showConfirm && (
        <div className="custom-confirm-overlay">
          <div className="custom-confirm-box">
            <p>
              Bazı bölümlerin yazısının eksik olduğu tespit edildi. Bu bölümlerin düzeltilmesini ister
              misiniz?
              <br />
              <small>Not: Diğer bölümler her iki durumda da aynı kalacaktır.</small>
            </p>
            <div className="custom-confirm-buttons">
              <button onClick={handleConfirmYes}>
                Eksik Bölümleri Yapay Zeka ile Düzelt
              </button>
              <button onClick={handleConfirmNo}>
                Proje Taslağıma Git
              </button>
              <button onClick={() => setShowConfirm(false)}>
                Geri Git
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatPage;